export type Action<Type extends string, Payload = undefined, Meta = undefined> = Payload extends undefined
    ? Meta extends undefined
        ? { type: Type }
        : { type: Type; meta: Meta }
    : Meta extends undefined
    ? { type: Type; payload: Payload }
    : { type: Type; payload: Payload; meta: Meta };

type ActionCreator<Type extends string, TAction extends Action<Type, any, any>, Args extends any[] = any[]> = ((
    ...args: Args
) => TAction) & { type: Type };

/**
 * More specific ReturnType
 */
export type ActionType<TActionCreator extends ActionCreator<any, any, any>> = ReturnType<TActionCreator>;

/**
 * Action Creator
 *
 * @property type       Required
 * @property payload    Optional
 * @property meta       Optional
 */
export function actionCreator<Type extends string>(type: Type): (() => Action<Type>) & { type: Type };
export function actionCreator<Type extends string, Args extends any[], Payload = undefined, Meta = undefined>(
    type: Type,
    payloadCreator?: (...args: Args) => Payload,
    metaCreator?: (...args: Args) => Meta
): ((...args: Args) => Action<Type, Payload, Meta>) & { type: Type };
export function actionCreator<Type extends string, Args extends any[], Payload = undefined, Meta = undefined>(
    type: Type,
    createPayload?: (...args: Args) => Payload,
    createMeta?: (...args: Args) => Meta
): ActionCreator<Type, Action<Type, Payload, Meta>, Args> {
    const creator = (...args: Args) => {
        const action: { type: Type; payload?: Payload; meta?: Meta } = { type };
        if (createPayload) action.payload = createPayload(...args);
        if (createMeta) action.meta = createMeta(...args);
        return action;
    };
    return Object.assign(creator as any, { type });
}
